import { ClickAwayListener } from '@mui/material';
import ChatPanelRuntime from 'components/Chat/ChatPanelRuntime';
import { CompactRightVariant } from 'components/Chat/ChatPromptSuggestions';
import { useDialog } from 'contexts/dialog';
import useInstructorHugoChat from 'hooks/chat/useInstructorHugoChat';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { IconButton, Loading, Stack, styled } from 'shell/components';
import { HugoPanelTransitionMs, LauncherInitialTopPx } from './constants';
import HugoLauncherControl from './HugoLauncherControl';
import HugoPanelFrame, { HugoPanelShell } from './HugoPanelFrame';
import HugoThreadSidebar from './HugoThreadSidebar';
import useHugoPanelAnimation from './useHugoPanelAnimation';
import useHugoThreadTransitions from './useHugoThreadTransitions';

const InstructorHugoChat = () => {
    const location = useLocation();
    const { t } = useTranslation();
    const {
        dialog: { open: isDialogOpen },
        setDialog,
        closeDialog,
    } = useDialog();

    const [isFullScreen, setIsFullScreen] = useState(false);
    const [launcherVerticalPosition, setLauncherVerticalPosition] = useState(LauncherInitialTopPx);
    const [isThreadSidebarExpanded, setIsThreadSidebarExpanded] = useState(false);
    const { closePanel, isOpen, isPanelMounted, isPanelVisible, openPanel } = useHugoPanelAnimation({
        transitionMs: HugoPanelTransitionMs,
    });

    const {
        buildOutgoingRequest,
        chat,
        isBootstrapping,
        isChatRouteSynced,
        onChatRequestStateChange,
        onDeleteThread,
        showSuggestions,
        suggestions,
        suggestionsTitle,
        threadListAdapter,
        threadSwitcher,
        viewModel,
    } = useInstructorHugoChat({
        isOpen,
        pathname: location.pathname,
    });
    const chatViewKey = chat.id || chat.local_id;
    const {
        animatedThreadListAdapter,
        clearPendingThreadTransition,
        displayedThreadSwitcher,
        pendingThreadTransition,
        startOpenTransition,
    } = useHugoThreadTransitions({
        chat,
        chatViewKey,
        isChatRouteSynced,
        isThreadSidebarExpanded,
        setIsThreadSidebarExpanded,
        threadListAdapter,
        threadSwitcher,
    });

    const toggleThreadSidebar = () => setIsThreadSidebarExpanded((currentValue) => !currentValue);
    const toggleFullScreen = () => setIsFullScreen((currentValue) => !currentValue);
    const openChat = () => {
        startOpenTransition();
        openPanel();
    };
    const closeChat = () => {
        closePanel();
        setIsFullScreen(false);
        clearPendingThreadTransition();
        setIsThreadSidebarExpanded(false);
    };

    const handleDeleteClick = (threadId) => {
        if (!threadId) return;

        setDialog({
            open: true,
            message: t('hugo-chat.delete-title', 'Permanently Delete This Chat?'),
            description: t(
                'hugo-chat.delete-description',
                "This will delete the chat and all messages in it. You won't be able to recover it later."
            ),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.delete', 'Delete'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: async () => {
                await onDeleteThread(threadId);
                closeDialog();
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const displayViewModel = {
        ...viewModel,
        header: {
            ...viewModel.header,
            title: isThreadSidebarExpanded
                ? t('hugo-chat.conversations', 'Conversation History')
                : viewModel.header.title,
        },
    };
    return (
        <>
            {isPanelMounted ? (
                <ClickAwayListener
                    mouseEvent='onMouseDown'
                    touchEvent='onTouchStart'
                    onClickAway={() => {
                        if (!isDialogOpen && isPanelVisible) {
                            closeChat();
                        }
                    }}
                >
                    <HugoPanelFrame
                        isFullScreen={isFullScreen}
                        isVisible={isPanelVisible}
                        launcherVerticalPosition={launcherVerticalPosition}
                    >
                        <HugoPanelShell direction={'row'}>
                            <ChatPanelRuntime
                                beforeContent={
                                    <HugoThreadSidebar
                                        isExpanded={isThreadSidebarExpanded}
                                        threadSwitcher={displayedThreadSwitcher}
                                        onDeleteThread={handleDeleteClick}
                                    />
                                }
                                buildOutgoingRequest={buildOutgoingRequest}
                                chat={chat}
                                hasOlderMessages={false}
                                headerStartContent={
                                    <Stack direction='row' alignItems='center' spacing={1}>
                                        <ThreadHeaderButton
                                            aria-label={
                                                isThreadSidebarExpanded ? 'Close threads panel' : 'Open threads panel'
                                            }
                                            kind={isThreadSidebarExpanded ? 'chevronLeft' : 'hamburger'}
                                            size={'small'}
                                            variant={'square'}
                                            onClick={toggleThreadSidebar}
                                        />
                                    </Stack>
                                }
                                headerEndContent={
                                    <HugoHeaderActions
                                        isFullScreen={isFullScreen}
                                        onClose={closeChat}
                                        onToggleFullScreen={toggleFullScreen}
                                    />
                                }
                                hideBackButton
                                isBootstrapping={isBootstrapping}
                                isLoadingOlderMessages={false}
                                isOpen={isOpen}
                                onChatRequestStateChange={onChatRequestStateChange}
                                showSuggestions={showSuggestions}
                                suggestions={suggestions}
                                suggestionsTitle={suggestionsTitle}
                                suggestionsVariant={CompactRightVariant}
                                threadFallback={
                                    pendingThreadTransition ? (
                                        <ThreadTransitionLoading>
                                            <Loading />
                                        </ThreadTransitionLoading>
                                    ) : null
                                }
                                threadListAdapter={animatedThreadListAdapter}
                                viewModel={displayViewModel}
                            />
                        </HugoPanelShell>
                    </HugoPanelFrame>
                </ClickAwayListener>
            ) : (
                <HugoLauncherControl
                    verticalPosition={launcherVerticalPosition}
                    onOpen={openChat}
                    onVerticalPositionChange={setLauncherVerticalPosition}
                />
            )}
        </>
    );
};

const HugoHeaderActions = ({ isFullScreen, onClose, onToggleFullScreen }) => {
    return (
        <>
            <HeaderActionButton
                aria-label={isFullScreen ? 'Exit full screen Hugo chat' : 'Open Hugo chat full screen'}
                kind={isFullScreen ? 'fullScreenExit' : 'fullScreenEnter'}
                size={'small'}
                variant={'square'}
                onClick={onToggleFullScreen}
            />
            <HeaderActionButton
                aria-label={'Close Hugo instructor assistant'}
                kind={'exit'}
                size={'small'}
                variant={'square'}
                onClick={onClose}
            />
        </>
    );
};

const ThreadTransitionLoading = styled('div')(() => {
    return {
        flex: 1,
        minWidth: 0,
        minHeight: 0,
    };
});

const ThreadHeaderButton = styled(IconButton)(({ disabled }) => {
    return {
        width: '24px',
        flexShrink: 0,
        opacity: disabled ? 0.35 : 1,
        marginInline: '-8px 4px',
    };
});

const HeaderActionButton = styled(IconButton)(() => {
    return {
        flexShrink: 0,
    };
});

export default InstructorHugoChat;
